import React, { useRef, useState, useEffect } from 'react'
import Slide from '../../components/slide-new/slide';
import Infobox from '../../components/presentationbox/infobox';
import LabelPreviewer from '../other/3D/label-previewer';
import { setSourceImage, generateTexture } from '../other/3D/label-canvas';

const labelController = {
    updateTexture: null,
    isReady: false
}

const labelPresets = [
    { id: 1, name: 'Label 1', path: '/canlabels/alfa-label-03.jpg' },
    { id: 2, name: 'Label 2', path: '/canlabels/alfa-label-04.jpg' },
    { id: 3, name: 'Label 3', path: '/canlabels/alfa-label-06.jpg' },
    { id: 4, name: 'Label 4', path: '/canlabels/alfa-label-07.jpg' },
    { id: 5, name: 'Label 5', path: '/canlabels/alfa-label-09.jpg' },
    { id: 6, name: 'Label 6', path: '/canlabels/pixelated test-05.jpg' },
    { id: 7, name: 'Label 7', path: '/canlabels/pixelated test2-05.jpg' },
    { id: 8, name: 'Label 8', path: '/canlabels/quick-test-06.jpg' },
];

const finalLabel = { id: 'final', name: 'FINAL', path: '/canlabels/final final-05.jpg' };

const LabelControls = () => {
    // Temporary state for inputs (not applied until Apply button clicked)
    const [tempOffsetY, setTempOffsetY] = useState(54);
    const [tempScale, setTempScale] = useState(98);

    // Generated texture state
    const [generatedTexture, setGeneratedTexture] = useState(null);
    const [sourceImageLoaded, setSourceImageLoaded] = useState(false);
    const [selectedLabel, setSelectedLabel] = useState('final');

    const previewImageRef = useRef(null);
    const fileInputRef = useRef(null);

    const handleCanvasClick = () => {
        fileInputRef.current?.click();
    };

    const loadLabelImage = async (imagePath) => {
        const img = await setSourceImage(imagePath);
        setSourceImageLoaded(true);

        // Auto-generate with default settings (0% X, 54% Y, 98% scale)
        const textureUrl = generateTexture(0, 54, 98);
        setGeneratedTexture(textureUrl);

        // Update preview image
        if (previewImageRef.current) {
            previewImageRef.current.src = textureUrl;
        }

        // Update 3D can - retry if controller not ready yet
        const updateCan = () => {
            if (labelController.isReady && labelController.updateTexture) {
                labelController.updateTexture(textureUrl);
            } else {
                setTimeout(updateCan, 50);
            }
        };
        updateCan();
    };

    const handleLabelSelect = (labelId) => {
        setSelectedLabel(labelId);
        let preset;
        if (labelId === 'final') {
            preset = finalLabel;
        } else {
            preset = labelPresets.find(p => p.id === labelId);
        }
        if (preset) {
            loadLabelImage(preset.path);
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = async (event) => {
                await loadLabelImage(event.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleApply = () => {
        if (!sourceImageLoaded) return;

        // Generate new texture with current settings (X always 0)
        const textureUrl = generateTexture(0, tempOffsetY, tempScale);
        setGeneratedTexture(textureUrl);

        // Update preview image
        if (previewImageRef.current) {
            previewImageRef.current.src = textureUrl;
        }

        // Update 3D can
        labelController.updateTexture(textureUrl);
    };

    // Load final label by default on mount
    useEffect(() => {
        loadLabelImage(finalLabel.path);
    }, []);

    return (
        <div style={{ color: 'white' }}>
            {/* Final Label Button - Own Row */}
            <div style={{ marginBottom: '0.5em', display: 'flex', justifyContent: 'center' }}>
                <button
                    onClick={() => handleLabelSelect('final')}
                    style={{
                        width: 'auto',
                        padding: '8px 20px',
                        fontSize: '1em',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        pointerEvents: 'all',
                        backgroundColor: selectedLabel === 'final' ? '#E01283' : '#666',
                        color: 'white',
                        border: selectedLabel === 'final' ? '2px solid white' : '2px solid #888',
                        borderRadius: '5px'
                    }}
                >
                    FINAL
                </button>
            </div>

            {/* Other Label Selection Buttons */}
            <div style={{ marginBottom: '1em', display: 'flex', gap: '0.5em', justifyContent: 'center' }}>
                {labelPresets.map(preset => (
                    <button
                        key={preset.id}
                        onClick={() => handleLabelSelect(preset.id)}
                        style={{
                            width: '40px',
                            height: '40px',
                            fontSize: '1em',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            pointerEvents: 'all',
                            backgroundColor: selectedLabel === preset.id ? '#E01283' : '#666',
                            color: 'white',
                            border: selectedLabel === preset.id ? '2px solid white' : '2px solid #888',
                            borderRadius: '5px'
                        }}
                    >
                        {preset.id}
                    </button>
                ))}
            </div>

            <div style={{ display: 'flex', gap: '0.7em', alignItems: 'flex-start' }}>
                {/* Image Preview */}
                <div style={{ flex: '0 0 auto' }}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                    />
                    <div
                        onClick={handleCanvasClick}
                        style={{
                            border: '2px solid white',
                            backgroundColor: '#C0C0C0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '160px',
                            height: '160px',
                            cursor: 'pointer',
                            pointerEvents: 'all',
                            overflow: 'hidden'
                        }}
                    >
                        {generatedTexture ? (
                            <img
                                ref={previewImageRef}
                                src={generatedTexture}
                                alt="Label preview"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain',
                                    imageRendering: 'pixelated'
                                }}
                            />
                        ) : (
                            <span style={{ fontSize: '0.75em', opacity: 0.7 }}>Loading...</span>
                        )}
                    </div>
                    {generatedTexture && (
                        <p style={{ fontSize: '0.75em', margin: '0.3em 0 0 0', opacity: 0.7 }}>
                            Click to upload custom
                        </p>
                    )}
                </div>

                {/* Controls Column */}
                <div style={{ flex: '1', minWidth: 0 }}>
                    <div style={{ marginBottom: '0.5em' }}>
                        <label style={{ fontSize: '0.85em', display: 'block', marginBottom: '2px' }}>
                            Scale (%)
                        </label>
                        <input
                            type="number"
                            min="1"
                            max="200"
                            value={tempScale}
                            onChange={(e) => setTempScale(parseInt(e.target.value) || 100)}
                            style={{
                                width: '100%',
                                padding: '4px',
                                fontSize: '0.85em',
                                pointerEvents: 'all',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '0.5em' }}>
                        <label style={{ fontSize: '0.85em', display: 'block', marginBottom: '2px' }}>
                            Y (%)
                        </label>
                        <input
                            type="number"
                            min="-100"
                            max="100"
                            value={tempOffsetY}
                            onChange={(e) => setTempOffsetY(parseInt(e.target.value) || 0)}
                            style={{
                                width: '100%',
                                padding: '4px',
                                fontSize: '0.85em',
                                pointerEvents: 'all',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>

                    <button
                        onClick={handleApply}
                        style={{
                            width: '100%',
                            padding: '6px',
                            fontSize: '0.85em',
                            pointerEvents: 'all',
                            boxSizing: 'border-box',
                            cursor: 'pointer',
                            backgroundColor: '#E01283',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            fontWeight: 'bold'
                        }}
                    >
                        Apply
                    </button>
                </div>
            </div>
        </div>
    );
};

const contents = [
    {
        name: "GLHF",
        icon: "spaceinvader",
        element: <Infobox title="Preview" text={<LabelControls />} />,
        element3d: <LabelPreviewer labelController={labelController}/>
    },
];

const Label = () => {
    return (
        <Slide contents={contents} theme="new3"/>
    )
}

export default Label
